import { api } from './api';

export interface UploadedFile {
  /** Public hosted URL to persist on the schema (stored on element/audio). */
  url: string;
  /** Upstream file id (when provided by the upload service). */
  fileId?: string;
  key: string;
  baseUrl: string;
  /** Mime type the file was uploaded with. */
  contentType: string;
  /** Final file name used on the upload. */
  name: string;
}

export interface UploadOptions {
  /** Logical bucket / category. Defaults to 'ADMIN'. */
  type?: string;
  /** Override file name (without extension). Defaults to the file's name. */
  name?: string;
  /** Override extension (without leading dot). Defaults to the file's extension. */
  extension?: string;
  /** Override mime type. Defaults to `file.type` (or 'application/octet-stream'). */
  contentType?: string;
  /** Optional progress callback (0..1). */
  onProgress?: (fraction: number) => void;
}

/**
 * High-level helper: ask the backend for a signed URL, then PUT the file
 * directly to S3, returning the public hosted URL to persist.
 */
export async function uploadFile(file: Blob | File, opts: UploadOptions = {}): Promise<UploadedFile> {
  const fileName = opts.name ?? (file as File).name ?? 'upload';
  const guessedExt = inferExtension(fileName, file.type);
  const contentType = opts.contentType ?? file.type ?? 'application/octet-stream';

  const signed = await api.uploads.getSignedUrl({
    type: opts.type ?? 'ADMIN',
    name: stripExtension(fileName),
    extension: opts.extension ?? guessedExt,
    contentType,
  });

  await putToSignedUrl(signed.uploadUrl, file, contentType, opts.onProgress);

  return {
    url: signed.url,
    fileId: signed.fileId,
    key: signed.key,
    baseUrl: signed.baseUrl,
    contentType,
    name: signed.name ?? fileName,
  };
}

function putToSignedUrl(
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  // We use XHR (not fetch) because it exposes progress events for the upload
  // body, which lets us drive a progress UI for large media files.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      });
    }
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new Error(`Upload failed with ${xhr.status} ${xhr.statusText}`));
      }
    });
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
    xhr.send(file);
  });
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

function inferExtension(name: string, mimeType: string | undefined): string {
  const dot = name.lastIndexOf('.');
  if (dot > 0 && dot < name.length - 1) return name.slice(dot + 1).toLowerCase();
  switch ((mimeType ?? '').toLowerCase()) {
    case 'image/png':
      return 'png';
    case 'image/jpeg':
    case 'image/jpg':
      return 'jpg';
    case 'image/gif':
      return 'gif';
    case 'image/webp':
      return 'webp';
    case 'image/svg+xml':
      return 'svg';
    case 'video/mp4':
      return 'mp4';
    case 'video/webm':
      return 'webm';
    case 'video/quicktime':
      return 'mov';
    case 'audio/mpeg':
    case 'audio/mp3':
      return 'mp3';
    case 'audio/wav':
    case 'audio/wave':
      return 'wav';
    case 'audio/webm':
      return 'webm';
    case 'audio/ogg':
      return 'ogg';
    case 'audio/mp4':
    case 'audio/m4a':
      return 'm4a';
    case 'model/gltf-binary':
      return 'glb';
    case 'model/gltf+json':
      return 'gltf';
    default:
      return 'bin';
  }
}
